import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import VerifiedLeadsTable from '../components/VerifiedLeadsTable';
import SessionManager from '../components/SessionManager';
import SafeIcon from '../common/SafeIcon';
import { FaUsers, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

const VerifiedLeads = ({ onLeadsSelected, currentSessionId }) => {
  const [selectedSessionId, setSelectedSessionId] = useState(currentSessionId || null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (currentSessionId) {
      setSelectedSessionId(currentSessionId);
    }
  }, [currentSessionId]);

  const handleSessionSelect = (sessionId, sessionResults, sessionName) => {
    console.log('Session selected:', sessionId, sessionResults, sessionName);
    setSelectedSessionId(sessionId);
    
    // Find the session info from the sessions list
    const session = sessions.find(s => s.session_id === sessionId);
    if (session) {
      setSessionInfo(session);
    } else {
      // If session not found in list, create a basic session info object
      setSessionInfo({
        session_id: sessionId,
        session_name: sessionName || `Session ${sessionId}`,
        verified_leads: sessionResults?.length || 0,
        safe_leads: sessionResults?.filter(r => r.status === 'safe')?.length || 0
      });
    }
  };

  const handleSessionsLoaded = (loadedSessions) => {
    setSessions(loadedSessions);
  };

  const handleLeadsSelected = (selectedLeads) => {
    console.log('Leads selected for analysis:', selectedLeads);
    if (onLeadsSelected) {
      onLeadsSelected(selectedLeads);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.1 }}
      className="verified-leads-page"
    >
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title">
              <SafeIcon icon={FaUsers} className="title-icon" />
              Verified Leads Management
            </h1>
            <p className="page-description">
              Select and manage your verified leads for website analysis and opener generation.
            </p>
          </div>
          
          {sessionInfo && (
            <div className="session-stats">
              <div className="stat-card">
                <SafeIcon icon={FaCheckCircle} className="stat-icon verified" />
                <div className="stat-content">
                  <span className="stat-number">{sessionInfo.verified_leads || 0}</span>
                  <span className="stat-label">Verified Leads</span>
                </div>
              </div>
              <div className="stat-card">
                <SafeIcon icon={FaCheckCircle} className="stat-icon safe" />
                <div className="stat-content">
                  <span className="stat-number">{sessionInfo.safe_leads || 0}</span>
                  <span className="stat-label">Safe to Send</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="content-container">
        <div className="session-selection">
          <h3>Select Verification Session</h3>
          <p>Choose a session to view and manage its verified leads.</p>
          
          <SessionManager 
            onSessionSelect={handleSessionSelect}
            currentSessionId={selectedSessionId}
            onSessionsLoaded={handleSessionsLoaded}
            filterByType="email_verification"
          />
        </div>

        {selectedSessionId ? (
          <div className="leads-section">
            <VerifiedLeadsTable 
              sessionId={selectedSessionId}
              onLeadsSelected={handleLeadsSelected}
            />
          </div>
        ) : (
          <div className="no-session-selected">
            <SafeIcon icon={FaExclamationTriangle} className="warning-icon" />
            <h3>No Session Selected</h3>
            <p>Please select a verification session above to view and manage verified leads.</p>
          </div>
        )}
      </div>

      <style jsx="true">{`
        .verified-leads-page {
          min-height: 100vh;
          background: #f8fafc;
        }

        .page-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 2rem 0;
        }

        .header-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 2rem;
        }

        .header-text {
          flex: 1;
        }

        .page-title {
          margin: 0 0 0.5rem 0;
          font-size: 2rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .title-icon {
          font-size: 1.75rem;
        }

        .page-description {
          margin: 0;
          font-size: 1.1rem;
          opacity: 0.9;
          line-height: 1.5;
        }

        .session-stats {
          display: flex;
          gap: 1rem;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 12px;
          padding: 1rem 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .stat-icon {
          font-size: 1.5rem;
        }

        .stat-icon.verified {
          color: #10b981;
        }

        .stat-icon.safe {
          color: #3b82f6;
        }

        .stat-content {
          display: flex;
          flex-direction: column;
        }

        .stat-number {
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1;
        }

        .stat-label {
          font-size: 0.85rem;
          opacity: 0.8;
          margin-top: 0.25rem;
        }

        .content-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
        }

        .session-selection {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .session-selection h3 {
          margin: 0 0 0.5rem 0;
          color: #1f2937;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .session-selection p {
          margin: 0 0 1rem 0;
          color: #6b7280;
          font-size: 0.95rem;
        }

        .leads-section {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        .no-session-selected {
          background: white;
          border-radius: 12px;
          padding: 3rem;
          text-align: center;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .warning-icon {
          font-size: 3rem;
          color: #f59e0b;
          margin-bottom: 1rem;
        }

        .no-session-selected h3 {
          margin: 0 0 0.5rem 0;
          color: #1f2937;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .no-session-selected p {
          margin: 0;
          color: #6b7280;
          font-size: 1rem;
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            text-align: center;
          }

          .page-title {
            font-size: 1.75rem;
          }

          .session-stats {
            flex-direction: column;
            width: 100%;
          }

          .stat-card {
            justify-content: center;
          }

          .content-container {
            padding: 1rem;
          }

          .session-selection {
            padding: 1rem;
          }

          .no-session-selected {
            padding: 2rem 1rem;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default VerifiedLeads;